import React, { useState } from 'react';
import Modal from './Modal';
import { getCustomerHistory, createNewBooking } from '../api/appointments';
import { useAgeSizes } from '../api/meta';

// ── New Booking Modal ─────────────────────────────────────────────────────
// A shop-at-home booking typed in by staff (one kid per booking). Created
// through the Skipzy app's booking API like a website booking — the app
// sends the appointment into ZOCS. "Fetch from history" pre-fills name,
// address and kid preferences from the customer's past bookings.

const OCCASIONS = ['Everyday & Play', 'Party', 'Birthday', 'Festive', 'Wedding', 'Outing & Travel', 'Nightwear', 'Winter'];

// On-the-hour slots only — the app books whole hours.
const SLOTS = Array.from({ length: 12 }, (_, i) => {
  const fmt = h => `${((h + 11) % 12) + 1}:00 ${h < 12 ? 'AM' : 'PM'}`;
  return `${fmt(9 + i)} - ${fmt(10 + i)}`;
});

const EMPTY = {
  parent_name: '', phone: '', address_line_1: '', address_line_2: '', area: '', pincode: '',
  google_maps_link: '', size: '', gender: 'girl', date: '', time: '', brands: '', occasion: '',
  price_range: '', styles: '', pieces: '', discount_code: '', comments: '',
};

function newKey() {
  return (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

export default function NewBookingModal({ onClose, onCreated }) {
  const ageSizes = useAgeSizes();
  const [form, setForm] = useState(EMPTY);
  const [idemKey] = useState(newKey);
  const [history, setHistory] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zoddle-green';
  const labelCls = 'block text-xs font-semibold text-gray-500 mb-1';
  const chipCls = 'text-left border border-gray-200 rounded-lg px-3 py-2 text-xs hover:border-zoddle-green hover:bg-green-50 transition';

  async function handleFetch() {
    setFetching(true); setError(null); setHistory(null);
    try {
      const h = await getCustomerHistory(form.phone);
      setHistory(h);
      if (h.found && h.name && !form.parent_name.trim()) set('parent_name', h.name);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not fetch history');
    } finally { setFetching(false); }
  }

  function fillAddress(a) {
    setForm(f => ({ ...f, address_line_1: a.address_line_1 || '', address_line_2: a.address_line_2 || '',
      area: a.area || '', pincode: a.pincode || '', google_maps_link: a.google_maps_link || '' }));
  }

  function fillKid(k) {
    setForm(f => ({ ...f, size: k.size || '', gender: k.gender === 'boy' ? 'boy' : 'girl',
      brands: (k.brands || []).join(', '), price_range: k.price_range || '', styles: k.styles || '' }));
  }

  async function handleCreate() {
    const f = form;
    if (!f.parent_name.trim() || !f.phone.trim() || !f.address_line_1.trim() || !f.pincode.trim()) {
      setError('Name, phone, address and pincode are required.'); return;
    }
    if (!f.size || !f.date || !f.time) { setError('Age, date and time slot are required.'); return; }
    setSaving(true); setError(null);
    try {
      const res = await createNewBooking({
        ...f, brands: f.brands.split(',').map(b => b.trim()).filter(Boolean), idempotency_key: idemKey,
      });
      setCreated(res);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Failed to create the booking');
    } finally { setSaving(false); }
  }

  if (created) {
    return (
      <Modal onClose={onCreated}>
        <div className="p-6 w-full max-w-lg space-y-4">
          <h2 className="text-lg font-bold text-gray-800">New booking</h2>
          <p className="text-sm text-green-700">
            ✓ Created <span className="font-mono font-semibold">{created.zcp_appointment_uid}</span>.
            {created.extras_saved
              ? ' It is in ZOCS now.'
              : ' It shows up in ZOCS within a few seconds — area, maps link and comments were not saved, add them on the booking.'}
            {' '}Use this ID when logging its sale or RTO.
          </p>
          <button onClick={onCreated}
            className="w-full bg-zoddle-green text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 transition">
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose}>
      <div className="p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-gray-800 mb-1">New booking</h2>
        <p className="text-xs text-gray-400 mb-4">
          Shop-at-home, one kid. Booked through the Skipzy app like any other booking. For a sibling, Duplicate it afterwards.
        </p>

        <div className="space-y-4">
          {/* Customer */}
          <div>
            <label className={labelCls}>Phone</label>
            <div className="flex gap-2">
              <input type="tel" value={form.phone} onChange={e => { set('phone', e.target.value); setHistory(null); }}
                placeholder="10-digit number" className={inputCls} />
              <button onClick={handleFetch} disabled={fetching || form.phone.replace(/\D/g, '').length < 10}
                className="shrink-0 bg-zoddle-blue text-white text-sm font-semibold px-4 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
                {fetching ? 'Fetching…' : 'Fetch from history'}
              </button>
            </div>
          </div>

          {history && !history.found && (
            <p className="text-xs text-gray-500">No past bookings for this number — fill in the details below.</p>
          )}
          {history && history.found && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-3">
              <p className="text-xs text-gray-500">Found <span className="font-semibold text-gray-700">{history.name}</span>. Tap to fill.</p>
              {history.addresses.length > 0 && (
                <div>
                  <p className={labelCls}>Past addresses</p>
                  <div className="grid gap-2">
                    {history.addresses.map((a, i) => (
                      <button key={i} onClick={() => fillAddress(a)} className={chipCls}>
                        <span className="text-gray-700">{[a.address_line_1, a.address_line_2].filter(Boolean).join(', ')}</span>
                        <span className="text-gray-400"> · {[a.area, a.pincode].filter(Boolean).join(' ')}{a.last_used ? ` · last ${a.last_used}` : ''}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {history.kids.length > 0 && (
                <div>
                  <p className={labelCls}>Past kids</p>
                  <div className="grid gap-2">
                    {history.kids.map((k, i) => (
                      <button key={i} onClick={() => fillKid(k)} className={chipCls}>
                        <span className="text-gray-700 capitalize">{k.gender} · {k.size || 'age unknown'}</span>
                        <span className="text-gray-400"> · last {k.last_booking}{k.last_date ? ` on ${k.last_date}` : ''}</span>
                        {(k.styles || k.brands.length > 0) && (
                          <span className="block text-gray-400 truncate">{[k.brands.join(', '), k.styles].filter(Boolean).join(' · ')}</span>
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Check the age — the kid may have grown since.</p>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className={labelCls}>Parent name</label>
              <input type="text" value={form.parent_name} onChange={e => set('parent_name', e.target.value)} className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Address</label>
              <input type="text" value={form.address_line_1} onChange={e => set('address_line_1', e.target.value)}
                placeholder="House / flat, building, street" className={inputCls} />
            </div>
            <div className="col-span-2">
              <input type="text" value={form.address_line_2} onChange={e => set('address_line_2', e.target.value)}
                placeholder="Landmark (optional)" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Area</label>
              <input type="text" value={form.area} onChange={e => set('area', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Pincode</label>
              <input type="text" inputMode="numeric" maxLength={6} value={form.pincode}
                onChange={e => set('pincode', e.target.value.replace(/\D/g, ''))} className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Google Maps link (optional)</label>
              <input type="url" value={form.google_maps_link} onChange={e => set('google_maps_link', e.target.value)} className={inputCls} />
            </div>
          </div>

          {/* Kid */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Age</label>
              <select value={form.size} onChange={e => set('size', e.target.value)} className={inputCls}>
                <option value="">Select age</option>
                {ageSizes.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Gender</label>
              <select value={form.gender} onChange={e => set('gender', e.target.value)} className={inputCls}>
                <option value="girl">Girl</option>
                <option value="boy">Boy</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Date</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Time slot</label>
              <select value={form.time} onChange={e => set('time', e.target.value)} className={inputCls}>
                <option value="">Select slot</option>
                {SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Preferences */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Occasion (optional)</label>
              <select value={form.occasion} onChange={e => set('occasion', e.target.value)} className={inputCls}>
                <option value="">—</option>
                {OCCASIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Price range (optional)</label>
              <input type="text" value={form.price_range} onChange={e => set('price_range', e.target.value)}
                placeholder="e.g. ₹500 - ₹1000" className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Brands (optional)</label>
              <input type="text" value={form.brands} onChange={e => set('brands', e.target.value)}
                placeholder="Comma separated" className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Styles</label>
              <textarea rows={2} value={form.styles} onChange={e => set('styles', e.target.value)} className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Pieces they need (optional)</label>
              <input type="text" value={form.pieces} onChange={e => set('pieces', e.target.value)}
                placeholder="Zoddle barcodes, comma separated" className={inputCls} />
              <p className="text-xs text-gray-400 mt-1">Added to the start of Styles.</p>
            </div>
            <div>
              <label className={labelCls}>Discount code (optional)</label>
              <input type="text" value={form.discount_code} onChange={e => set('discount_code', e.target.value)} className={inputCls} />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Comments (optional)</label>
              <textarea rows={2} value={form.comments} onChange={e => set('comments', e.target.value)} className={inputCls} />
            </div>
          </div>

          {error && <p className="text-xs text-zoddle-pink">{error}</p>}
          <div className="flex gap-3 pt-1">
            <button onClick={handleCreate} disabled={saving}
              className="flex-1 bg-zoddle-green text-white text-sm font-semibold py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
              {saving ? 'Creating...' : 'Create booking'}
            </button>
            <button onClick={onClose}
              className="flex-1 border border-gray-200 text-gray-600 text-sm font-semibold py-2 rounded-lg hover:bg-gray-50 transition">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
